/**
 * Public entry / role-selection screen (shown at "/").
 *
 * Presents one card per role. Picking a role sends the user to the unified login screen
 * with that role as context, so /auth can greet them with the matching identity. The real
 * post-login destination still comes from the authenticated user's role (RBAC), not the
 * card they tapped — the role here is only a presentation hint.
 *
 * Only admin and support are listed here; patient, doctor and pharmacist sign in at their
 * own URL (/patient, /doctor, /pharmacist).
 *
 * Layout is responsive and count-agnostic: cards wrap in a centred row, so the set stays
 * balanced whether LANDING_ROLES holds two roles or five. Colours come from src/index.css.
 */

import { Link, useNavigate } from "react-router-dom";

import { roleLogin } from "../auth/AuthContext";
import { ROLE_LIST } from "../auth/roleMeta";
import { Logo } from "../components/Logo";

// Only the roles without a sign-in URL of their own get a card here.
const LANDING_ROLES = ROLE_LIST.filter((card) => roleLogin(card.role) === "/");

export function Landing() {
  const navigate = useNavigate();

  return (
    <main className="relative flex min-h-dvh flex-col overflow-hidden bg-sunken">
      {/* Brand rule across the top + soft brand glows behind the hero. The logo itself
          always sits on a light surface so the navy mark stays legible. */}
      <div className="brand-gradient absolute inset-x-0 top-0 h-1.5" aria-hidden />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-96 bg-linear-to-b from-brand-50 via-brand-50/50 to-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -top-32 -left-32 h-80 w-80 rounded-full bg-accent-100/60 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-24 top-40 h-72 w-72 rounded-full bg-brand-100/60 blur-3xl"
        aria-hidden
      />

      {/* Content anchors to the top of the page (not vertically centred) so the logo and
          heading are the first thing in view regardless of how many role cards there are. */}
      <div className="relative mx-auto flex w-full max-w-5xl flex-1 flex-col justify-start px-4 pt-10 pb-12 sm:px-6 sm:pt-14 sm:pb-16">
        <header className="mb-8 flex flex-col items-center text-center sm:mb-10">
          <Logo variant="stacked" size={72} withTagline className="mb-6" />
          <h1 className="text-2xl font-bold tracking-tight text-brand-900 sm:text-3xl">
            Welcome back
          </h1>
          <p className="mx-auto mt-2 max-w-md text-sm text-ink-muted sm:text-base">
            Choose how you’d like to continue.
          </p>
        </header>

        <section className="flex flex-wrap justify-center gap-4 sm:gap-5">
          {LANDING_ROLES.map((card) => (
            <button
              key={card.role}
              type="button"
              onClick={() => navigate("/auth", { state: { role: card.role } })}
              className="group relative flex w-full flex-col items-start gap-3 overflow-hidden rounded-2xl border border-line bg-surface p-5 text-left shadow-card transition duration-200
                         hover:-translate-y-1 hover:border-brand-200 hover:shadow-raised sm:w-[calc(50%-0.625rem)] sm:max-w-sm sm:p-6"
            >
              {/* Brand rule that slides in along the top edge on hover. */}
              <span
                className="brand-gradient absolute inset-x-0 top-0 h-1 origin-left scale-x-0 transition-transform duration-300 group-hover:scale-x-100"
                aria-hidden
              />

              <span
                className={`inline-flex h-12 w-12 items-center justify-center rounded-xl transition ${card.accent}`}
              >
                {card.icon}
              </span>
              <span className="mt-1 text-lg font-semibold text-brand-900">{card.title}</span>
              <span className="text-sm leading-relaxed text-ink-muted">{card.description}</span>

              {/* Always visible so touch users get the affordance too; animates on hover. */}
              <span className="mt-auto flex w-full items-center justify-between border-t border-line pt-4 text-sm font-semibold text-brand">
                Continue as {card.title}
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-50 text-brand transition group-hover:translate-x-0.5 group-hover:bg-brand group-hover:text-brand-fg">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </span>
              </span>
            </button>
          ))}
        </section>
      </div>

      <footer className="relative pb-6 text-center text-xs text-ink-muted">
        © {new Date().getFullYear()} EMEDIEXP · Express care. Always there. ·{" "}
        <Link to="/legal/data-deletion" className="hover:text-brand hover:underline">
          Delete account
        </Link>
      </footer>
    </main>
  );
}
